import { Action } from 'redux-actions'
import { all, call, put, takeEvery, take } from 'redux-saga/effects'
import { t } from 'ttag'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import api from '../../../services/api.services'
import {
  DeleteUser$Request,
  GetSpecificCompanyUsers$Request,
  SetUserCountry$Request,
  SetUserDivision$Request,
  ToggleUserStatus$Request,
  BulkDeleteUsers$Request,
  BulkAddAdminRole$Request,
  BulkRemoveAdminRole$Request,
} from '../../../services/api.types'
import {
  deleteUser,
  getCompanyUsers,
  getCompanyUsersFail,
  getCompanyUsersSuccess,
  setUserCountry,
  setUserDivision,
  setUserDivisionSuccess,
  toggleUserStatus,
  bulkDeleteUsers,
  bulkDeleteUsersStart,
  bulkDeleteUsersSuccess,
  bulkDeleteUsersFail,
  bulkAddAdminRole,
  bulkAddAdminRoleStart,
  bulkAddAdminRoleSuccess,
  bulkAddAdminRoleFail,
  bulkRemoveAdminRole,
  bulkRemoveAdminRoleStart,
  bulkRemoveAdminRoleSuccess,
  bulkRemoveAdminRoleFail,
} from './UserManagementActions'
import { getErrorMsg } from '../../../common/helpers/translations'
import { getCompanyDivisions } from '../../Settings/store/SettingsActions'

function* sagaGetCompanyUsers(action: Action<GetSpecificCompanyUsers$Request>) {
  try {
    const { companyUsers } = yield call(api.users.getSpecificCompanyUsers, action.payload)
    yield put({ type: String(getCompanyUsersSuccess), payload: companyUsers })
  } catch (e: any) {
    console.log('GetCompanyUsersFailed: ', e)
    yield put({ type: String(getCompanyUsersFail) })
  }
}

/*function* sagaCreateUser(action: Action<string[]>) {
  try {
    const emails = action.payload
    const { userDto } = yield api.auth.whoAmI()
    const { companyId } = userDto
    yield api.auth.createUser({ emails, lang: userDto.lang, companyId })

    const notification: INotification = {
      type: 'success',
      content: ngettext(
        msgid`User successfully created`,
        `Successfully created ${action.payload.length} users`,
        action.payload.length,
      ),
    }

    yield put(push(ROUTES.ADMIN_USER.to, { notification }))
  } catch (e: any) {
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('user creation'),
    }

    yield put({ type: String(addNotification), payload: notification })
  }
}*/

function* sagaDeleteUser(action: Action<DeleteUser$Request>) {
  try {
    yield api.users.deleteUser(action.payload)

    const { companyId } = action.payload
    yield put({ type: String(getCompanyUsers), payload: { companyId } })

    const deleteSucessNotification: INotification = {
      type: 'success',
      content: t`User successfully deleted!`,
    }

    yield put({ type: String(addNotification), payload: deleteSucessNotification })
  } catch (e: any) {
    const deleteFailsNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('user deletion'),
    }

    yield put({ type: String(addNotification), payload: deleteFailsNotification })
  }
}

function* sagaToggleUserStatus(action: Action<ToggleUserStatus$Request>) {
  try {
    yield api.users.toggleUserStatus(action.payload)
    yield put({ type: String(getCompanyUsers), payload: { companyId: action.payload.companyId } })
  } catch (e: any) {
    const toggleErrorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('user status change'),
    }

    yield put({ type: String(addNotification), payload: toggleErrorNotification })
  }
}

function* sagaSetUserCountry(action: Action<SetUserCountry$Request>) {
  try {
    yield api.users.setUserCountry(action.payload)
    yield put({ type: String(getCompanyUsers), payload: { companyId: action.payload.companyId } })
  } catch (e: any) {
    const toggleErrorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('user country change'),
    }

    yield put({ type: String(addNotification), payload: toggleErrorNotification })
  }
}

function* sagaSetUserDivision(action: Action<SetUserDivision$Request>) {
  try {
    yield api.users.setUserDivision(action.payload)
    yield put({ type: String(getCompanyUsers), payload: { companyId: action.payload.companyId } })
    yield put({ type: String(getCompanyDivisions), payload: { companyId: action.payload.companyId } })
    yield put({ type: String(setUserDivisionSuccess), payload: new Date().getTime() })
  } catch (e: any) {
    const toggleErrorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('user group change'),
    }
    yield put({ type: String(addNotification), payload: toggleErrorNotification })
  }
}

function* sagaBulkDeleteUsers(action: Action<BulkDeleteUsers$Request>) {
  try {
    yield put({ type: String(bulkDeleteUsersStart) })
    yield call(api.users.bulkDeleteUsers, action.payload)

    const { companyId } = action.payload
    yield put({ type: String(getCompanyUsers), payload: { companyId } })
    yield put({ type: String(bulkDeleteUsersSuccess) })

    const deleteSucessNotification: INotification = {
      type: 'success',
      content: t`Users successfully deleted!`,
    }

    yield put({ type: String(addNotification), payload: deleteSucessNotification })
  } catch (e: any) {
    yield put({ type: String(bulkDeleteUsersFail) })

    const deleteFailsNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('user deletion'),
    }

    yield put({ type: String(addNotification), payload: deleteFailsNotification })
  }
}

function* sagaBulkAddAdminRole(action: Action<BulkAddAdminRole$Request>) {
  try {
    yield put({ type: String(bulkAddAdminRoleStart) })
    yield call(api.users.bulkAddAdminRole, action.payload)

    const { companyId } = action.payload
    yield put({ type: String(getCompanyUsers), payload: { companyId } })

    yield take(String(getCompanyUsersSuccess))

    yield put({ type: String(bulkAddAdminRoleSuccess) })

    const successNotification: INotification = {
      type: 'success',
      content: t`Admin role successfully added!`,
    }

    yield put({ type: String(addNotification), payload: successNotification })
  } catch (e: any) {
    yield put({ type: String(bulkAddAdminRoleFail) })

    const failNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('role update'),
    }

    yield put({ type: String(addNotification), payload: failNotification })
  }
}

function* sagaBulkRemoveAdminRole(action: Action<BulkRemoveAdminRole$Request>) {
  try {
    yield put({ type: String(bulkRemoveAdminRoleStart) })
    yield call(api.users.bulkRemoveAdminRole, action.payload)

    const { companyId } = action.payload
    yield put({ type: String(getCompanyUsers), payload: { companyId } })

    yield take(String(getCompanyUsersSuccess))

    yield put({ type: String(bulkRemoveAdminRoleSuccess) })

    const successNotification: INotification = {
      type: 'success',
      content: t`Admin role successfully removed!`,
    }

    yield put({ type: String(addNotification), payload: successNotification })
  } catch (e: any) {
    yield put({ type: String(bulkRemoveAdminRoleFail) })

    const failNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('role update'),
    }

    yield put({ type: String(addNotification), payload: failNotification })
  }
}

export function* userManagementSagas() {
  yield all([
    takeEvery(String(getCompanyUsers), sagaGetCompanyUsers),
    //takeEvery(String(createUser), sagaCreateUser),
    takeEvery(String(deleteUser), sagaDeleteUser),
    takeEvery(String(toggleUserStatus), sagaToggleUserStatus),
    takeEvery(String(setUserCountry), sagaSetUserCountry),
    takeEvery(String(setUserDivision), sagaSetUserDivision),
    takeEvery(String(bulkDeleteUsers), sagaBulkDeleteUsers),
    takeEvery(String(bulkAddAdminRole), sagaBulkAddAdminRole),
    takeEvery(String(bulkRemoveAdminRole), sagaBulkRemoveAdminRole),
  ])
}
